import { useState, type Dispatch, type SetStateAction } from "react";
import { api } from "../../api/client.js";
import type { SelectedSkill, SkillDefinition, SkillPackDefinition } from "../../api/types.js";

type SkillCatalogResponse = {
  packs: SkillPackDefinition[];
  skills: SkillDefinition[];
};

export type UseSkillsState = {
  packs: SkillPackDefinition[];
  skills: SkillDefinition[];
  skillsLoading: boolean;
  skillSavingSlug: string | null;
  skillsError: string;
  selectedSkill: SelectedSkill | null;
  setSelectedSkill: Dispatch<SetStateAction<SelectedSkill | null>>;
  loadSkills: () => Promise<SkillDefinition[]>;
  toggleSkillPack: (pack: SkillPackDefinition, enabled: boolean) => Promise<void>;
};

export function useSkills(): UseSkillsState {
  const [packs, setPacks] = useState<SkillPackDefinition[]>([]);
  const [skills, setSkills] = useState<SkillDefinition[]>([]);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [skillSavingSlug, setSkillSavingSlug] = useState<string | null>(null);
  const [skillsError, setSkillsError] = useState("");
  const [selectedSkill, setSelectedSkill] = useState<SelectedSkill | null>(null);

  function applyCatalog(data: SkillCatalogResponse) {
    const nextPacks = data.packs || [];
    const nextSkills = data.skills || [];
    setPacks(nextPacks);
    setSkills(nextSkills);
    setSelectedSkill((selected) => selected && nextSkills.some((skill) => skill.name === selected.name) ? selected : null);
    return nextSkills;
  }

  async function loadSkills() {
    setSkillsLoading(true);
    setSkillsError("");
    try {
      return applyCatalog(await api<SkillCatalogResponse>("/api/skills"));
    } catch (error) {
      setSkillsError(error instanceof Error ? error.message : "技能加载失败");
      return [];
    } finally {
      setSkillsLoading(false);
    }
  }

  async function toggleSkillPack(pack: SkillPackDefinition, enabled: boolean) {
    setSkillSavingSlug(pack.slug);
    setPacks((old) => old.map((item) => item.slug === pack.slug ? { ...item, enabled } : item));
    if (!enabled) {
      setSkills((old) => old.filter((skill) => skill.packSlug !== pack.slug));
      if (selectedSkill && pack.members.some((member) => member.name === selectedSkill.name)) setSelectedSkill(null);
    }
    try {
      const data = await api<SkillCatalogResponse>(`/api/skills/packs/${pack.slug}`, {
        method: "PATCH",
        body: JSON.stringify({ enabled })
      });
      applyCatalog(data);
    } catch (error) {
      setSkillsError(error instanceof Error ? error.message : "技能更新失败");
      await loadSkills();
    } finally {
      setSkillSavingSlug(null);
    }
  }

  return {
    packs,
    skills,
    skillsLoading,
    skillSavingSlug,
    skillsError,
    selectedSkill,
    setSelectedSkill,
    loadSkills,
    toggleSkillPack
  };
}
